import { Fragment, useMemo } from "react";
import { QueryStatus, useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Globe } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { BrandGovernanceApiFactory, type CompetitorBenchmarkParams } from "@/apis/brand-os/brand-governance";
import type { CompetitorBenchmarkData } from "@/schema/brand-os/brand-governance";

const channelLeaderColor = "var(--foreground)";
const brandColor = "var(--destructive)";

function ChannelGapLeaderComponent({ status, benchmarkData }: { status: QueryStatus; benchmarkData?: CompetitorBenchmarkData | null }) {
  switch (status) {
    case "pending":
      return <ChannelGapLeaderSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      if (!benchmarkData?.competitors?.length) {
        return <LockedContent message={message} />;
      }
      return <ChannelGapLeaderContent data={benchmarkData} />;
  }
}

export function ChannelGapLeader({ params }: { params: CompetitorBenchmarkParams }) {
  const { data, status } = useQuery({
    ...BrandGovernanceApiFactory.Queries.CompetitorBenchmark(params),
    enabled: Boolean(params.brand_name),
  });

  return (
    <Card className="w-full gap-3 rounded-2xl bg-card shadow-none">
      <CardHeader className="flex items-center gap-2 pb-2">
        <span className="flex size-8 items-center justify-center rounded-lg bg-muted">
          <Globe className="size-4 text-muted-foreground" />
        </span>
        <CardTitle>Channel Gap to Leader</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ChannelGapLeaderComponent status={status} benchmarkData={data?.data} />
      </CardContent>
    </Card>
  );
}

function ChannelGapLeaderContent({ data }: { data: CompetitorBenchmarkData }) {
  const channelGapToLeader = data.benchmark?.channel_gap_to_leader;
  const brandName = data.brand_name ?? "Brand";

  const chartConfig: ChartConfig = useMemo(
    () => ({
      leader: { label: "Channel Leader", color: channelLeaderColor },
      brand: { label: brandName, color: brandColor },
    }),
    [brandName]
  );

  const chartData = useMemo(() => {
    if (!channelGapToLeader) return [];
    return Object.entries(channelGapToLeader).map(([channel, metric]) => ({
      channel,
      leader: metric.leader.avg_score,
      brand: metric.brand_value.avg_score,
    }));
  }, [channelGapToLeader]);

  const hasMeaningfulScores = chartData.some((row) => row.leader > 0 || row.brand > 0);
  if (!chartData.length || !hasMeaningfulScores) {
    return <LockedContent message={message} className="my-8" />;
  }

  return (
    <Fragment>
      <ChartContainer config={chartConfig} className="h-64 w-full">
        <BarChart accessibilityLayer data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
          <XAxis dataKey="channel" tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
          <YAxis tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
          <ChartTooltip cursor={false} content={<ChartTooltipContent className="min-w-44 font-semibold" />} />
          <Bar dataKey="leader" name="Channel Leader" fill={channelLeaderColor} radius={[3, 3, 0, 0]} barSize={24} />
          <Bar dataKey="brand" name={brandName} fill={brandColor} radius={[3, 3, 0, 0]} barSize={24} />
        </BarChart>
      </ChartContainer>
      <div className="mt-2 flex items-center justify-center gap-6 border-t border-border pt-4">
        <div className="flex items-center gap-2">
          <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: brandColor }} aria-hidden />
          <span className="text-sm text-muted-foreground">{brandName}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: channelLeaderColor }} aria-hidden />
          <span className="text-sm text-muted-foreground">Channel Leader</span>
        </div>
      </div>
    </Fragment>
  );
}

function ChannelGapLeaderSkeleton() {
  return (
    <Fragment>
      <div className="flex flex-col justify-end space-y-4 pb-8">
        {[60, 45, 75, 50, 35].map((w, i) => (
          <Skeleton key={i} className="h-6 rounded-md" style={{ width: `${w}%` }} />
        ))}
      </div>
      <div className="mt-2 flex items-center justify-center gap-6 border-t border-border pt-4">
        <div className="flex items-center gap-2">
          <Skeleton className="size-2.5 rounded-full" />
          <Skeleton className="h-4 w-16 rounded-md" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="size-2.5 rounded-full" />
          <Skeleton className="h-4 w-24 rounded-md" />
        </div>
      </div>
    </Fragment>
  );
}
